// Network's review tasks (1.45, 1.46), worked on Network › Actions › Reviews and on Home.
// - post_review_reply: an approved reply to paste under the review (no Google Business Profile connection yet). Marking
//   it replied (crm_mark_review_replied) records the reply and closes the task.
// - review_feedback: a customer answered the review request unhappily. The owner gets in touch; the request stands.
// The customer's words are their own: shown as text, never as instructions.
import { useId, useState } from "react";
import { Link } from "react-router-dom";
import type { Crm } from "../../lib/crm";
import type { HumanTask } from "../../lib/types";
import { webUrl } from "../../lib/format";
import { Notice } from "../../components/ui";

const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function postReviewReplySummary(t: HumanTask): string {
  return t.payload.title ? String(t.payload.title) : "Post your reply under the review";
}

export function PostReviewReplyTask({ crm, task, onDone }: { crm: Crm; task: HumanTask; onDone: () => void }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = useId();
  const review = String(task.payload.review_id ?? task.entity_id ?? "");
  const text = typeof task.payload.copy_text === "string" ? task.payload.copy_text : "";
  const link = webUrl(typeof task.payload.review_url === "string" ? task.payload.review_url : null);
  return (
    <div>
      <p className="muted small">The Google Business Profile connection is not set up yet: paste this reply under the review, then mark it replied.</p>
      {text && <blockquote className="quote">{text}</blockquote>}
      <div className="action-bar">
        {text && (
          <button
            type="button"
            className="btn"
            onClick={() => {
              void navigator.clipboard?.writeText(text).then(
                () => setCopied(true),
                () => setCopied(false),
              );
            }}
          >
            {copied ? "Copied" : "Copy the reply"}
          </button>
        )}
        {link && (
          <a className="btn btn-quiet" href={link} target="_blank" rel="noreferrer">
            Open the review
          </a>
        )}
      </div>
      <label className="field" htmlFor={id}>
        <span className="field-label">Link to your posted reply (optional)</span>
        <input id={id} type="url" inputMode="url" placeholder="https://" value={url} onChange={(e) => setUrl(e.target.value)} />
      </label>
      <Notice text={error} tone="bad" />
      <div className="action-bar">
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy || !review}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await crm.markReviewReplied(review, url);
              onDone();
            } catch (e) {
              setError(errorText(e));
              setBusy(false);
            }
          }}
        >
          {busy ? "Saving…" : "Mark as replied"}
        </button>
      </div>
    </div>
  );
}

export function reviewFeedbackSummary(t: HumanTask): string {
  return t.payload.title ? String(t.payload.title) : "A customer replied unhappily to the review request";
}

export function ReviewFeedbackTask({ crm, task, onDone }: { crm: Crm; task: HumanTask; onDone: () => void }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = useId();
  const person = typeof task.payload.person_id === "string" ? task.payload.person_id : null;
  const summary = typeof task.payload.summary === "string" ? task.payload.summary : null;
  return (
    <div>
      {summary ? <blockquote className="quote">{summary}</blockquote> : <p className="muted small">No summary of what they said.</p>}
      <p className="muted small">Get in touch with them yourself. Their review request stands: it is never withdrawn or held back because of this.</p>
      {person && (
        <p>
          <Link to={`/contacts/people/${person}`}>Open their page</Link>
        </p>
      )}
      <label className="field" htmlFor={id}>
        <span className="field-label">What you did (optional)</span>
        <textarea id={id} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <Notice text={error} tone="bad" />
      <div className="action-bar">
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await crm.completeTask(task.id, "done", note.trim() ? { note: note.trim().slice(0, 2000) } : {});
              onDone();
            } catch (e) {
              setError(errorText(e));
              setBusy(false);
            }
          }}
        >
          {busy ? "Saving…" : "Mark as done"}
        </button>
      </div>
    </div>
  );
}
